/**
 * Filter Configuration Types and Utilities
 *
 * Defines the structure for configuring filterable fields in the dataset viewer.
 * Supports various filter types: multiSelect, range, dateRange, select, text, boolean
 */

export type FilterType =
  | "select" // Single select dropdown
  | "multiSelect" // Checkboxes for multiple values
  | "range" // Numeric range slider
  | "dateRange" // Date range picker with presets
  | "boolean" // Yes/No toggle
  | "text" // Free text input with contains search
  | "histogram"; // Read-only histogram showing value distribution

export interface FilterOption {
  /** The value sent to the API */
  value: string;
  /** Display label for the UI */
  label: string;
  /** Optional count of items matching this value */
  count?: number;
  /** Optional icon to display */
  icon?: React.ReactNode;
}

export interface DatePreset {
  /** Display label (e.g., "Last 7 days") */
  label: string;
  /** Key for the preset (used in URL) */
  key: string;
  /** Function that returns the date range */
  getValue: () => { start: Date; end: Date };
}

export interface RangeConfig {
  /** Minimum value */
  min?: number;
  /** Maximum value */
  max?: number;
  /** Step increment */
  step?: number;
  /** Unit label (e.g., "$", "MB", "%") */
  unit?: string;
  /** Position of unit: before ($100) or after (100 MB) */
  unitPosition?: "before" | "after";
  /** Format function for display */
  formatValue?: (value: number) => string;
}

export interface FilterFieldConfig {
  /** Field name in the data (e.g., "status", "file_type") */
  fieldName: string;

  /** Display label (e.g., "Status", "File Type") */
  label: string;

  /** Type of filter control to render */
  type: FilterType;

  /** URL parameter name (defaults to fieldName) */
  urlParam?: string;

  /** For select/multiSelect: predefined options */
  options?: FilterOption[];

  /** For select/multiSelect: fetch options from API endpoint */
  optionsEndpoint?: string;

  /** For select/multiSelect: derive options from data if no endpoint/options */
  deriveFromData?: boolean;

  /** For range filters: configuration */
  rangeConfig?: RangeConfig;

  /** For dateRange: preset options */
  datePresets?: DatePreset[];

  /** Default expanded state in sidebar (default: true for first 3) */
  defaultExpanded?: boolean;

  /** Sort order in sidebar (lower = higher) */
  order?: number;

  /** Group/category for organizing filters */
  group?: string;

  /** Whether this filter is always visible (not collapsible) */
  alwaysVisible?: boolean;

  /** Placeholder text for text inputs */
  placeholder?: string;

  /** Help text shown below the filter */
  helpText?: string;

  /** Maximum number of options to show before "Show more" */
  maxVisibleOptions?: number;

  /** Whether to show search box for long option lists */
  searchable?: boolean;

  /** Whether to show counts next to options */
  showCounts?: boolean;

  /** Custom render function for options */
  renderOption?: (option: FilterOption) => React.ReactNode;

  /** For histogram: orientation of bars */
  histogramOrientation?: "horizontal" | "vertical";

  /** For histogram: whether to show percentages instead of counts */
  histogramShowPercentages?: boolean;
}

export interface FilterGroup {
  /** Unique identifier for the group */
  id: string;
  /** Display label for the group */
  label: string;
  /** Filters in this group */
  filters: FilterFieldConfig[];
  /** Default collapsed state */
  defaultCollapsed?: boolean;
  /** Sort order */
  order?: number;
}

// ============================================================================
// Default Date Presets
// ============================================================================

export const DEFAULT_DATE_PRESETS: DatePreset[] = [
  {
    label: "Today",
    key: "today",
    getValue: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const end = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23,
        59,
        59,
        999,
      );
      return { start, end };
    },
  },
  {
    label: "Yesterday",
    key: "yesterday",
    getValue: () => {
      const now = new Date();
      const start = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - 1,
      );
      const end = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - 1,
        23,
        59,
        59,
        999,
      );
      return { start, end };
    },
  },
  {
    label: "Last 7 days",
    key: "last7days",
    getValue: () => {
      const now = new Date();
      const start = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - 7,
      );
      const end = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23,
        59,
        59,
        999,
      );
      return { start, end };
    },
  },
  {
    label: "Last 30 days",
    key: "last30days",
    getValue: () => {
      const now = new Date();
      const start = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - 30,
      );
      const end = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23,
        59,
        59,
        999,
      );
      return { start, end };
    },
  },
  {
    label: "Last 90 days",
    key: "last90days",
    getValue: () => {
      const now = new Date();
      const start = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() - 90,
      );
      const end = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23,
        59,
        59,
        999,
      );
      return { start, end };
    },
  },
  {
    label: "This year",
    key: "thisyear",
    getValue: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), 0, 1);
      const end = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23,
        59,
        59,
        999,
      );
      return { start, end };
    },
  },
];

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Infers the best filter type for a field based on its name and values
 */
export function inferFilterType(
  fieldName: string,
  sampleValues?: unknown[],
): FilterType {
  const lowerName = fieldName.toLowerCase();

  // Date fields
  if (
    lowerName.includes("date") ||
    lowerName.includes("time") ||
    lowerName.includes("created") ||
    lowerName.includes("updated") ||
    lowerName.includes("modified")
  ) {
    return "dateRange";
  }

  // Boolean fields
  if (
    lowerName.includes("is_") ||
    lowerName.includes("has_") ||
    lowerName.includes("can_") ||
    lowerName === "active" ||
    lowerName === "enabled" ||
    lowerName === "visible"
  ) {
    return "boolean";
  }

  // Numeric/range fields - show as histogram (read-only distribution)
  if (
    lowerName.includes("price") ||
    lowerName.includes("amount") ||
    lowerName.includes("count") ||
    lowerName.includes("size") ||
    lowerName.includes("number") ||
    lowerName.includes("quantity") ||
    lowerName.includes("total") ||
    lowerName.includes("score") ||
    lowerName.includes("confidence")
  ) {
    return "histogram";
  }

  // Status/type fields are good for multiSelect
  if (
    lowerName === "status" ||
    lowerName === "type" ||
    lowerName === "category" ||
    lowerName.includes("_type") ||
    lowerName.includes("_status")
  ) {
    return "multiSelect";
  }

  // Check sample values if provided
  if (sampleValues && sampleValues.length > 0) {
    const uniqueValues = new Set(sampleValues.map(String));

    // If there are few unique values, use multiSelect
    if (uniqueValues.size <= 10) {
      return "multiSelect";
    }

    // If all values are numbers, use histogram
    if (sampleValues.every((v) => typeof v === "number" || !isNaN(Number(v)))) {
      return "histogram";
    }
  }

  // Default to text for free-form search
  return "text";
}

/**
 * Creates a filter config from a field name with sensible defaults
 */
export function createFilterConfig(
  fieldName: string,
  overrides?: Partial<FilterFieldConfig>,
): FilterFieldConfig {
  const type = inferFilterType(fieldName);

  const config: FilterFieldConfig = {
    fieldName,
    label: prettifyLabel(fieldName),
    type,
    deriveFromData: type === "multiSelect" || type === "select",
    defaultExpanded: false,
    showCounts: true,
    maxVisibleOptions: 5,
    searchable: true,
    ...overrides,
  };

  // Add default date presets for date fields
  if (type === "dateRange" && !config.datePresets) {
    config.datePresets = DEFAULT_DATE_PRESETS;
  }

  return config;
}

/**
 * Prettifies a field name into a display label
 */
export function prettifyLabel(fieldName: string): string {
  // Special cases
  const specialCases: Record<string, string> = {
    id: "ID",
    uid: "UID",
    url: "URL",
    api: "API",
    fk: "ID",
    pk: "ID",
  };

  return fieldName
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .trim()
    .split(" ")
    .map((word) => {
      const lower = word.toLowerCase();
      if (specialCases[lower]) return specialCases[lower];
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

/**
 * Groups filter configs by their group property
 */
export function groupFilterConfigs(
  configs: FilterFieldConfig[],
): FilterGroup[] {
  const grouped = new Map<string, FilterFieldConfig[]>();

  configs.forEach((config) => {
    const group = config.group || "default";
    if (!grouped.has(group)) {
      grouped.set(group, []);
    }
    grouped.get(group)!.push(config);
  });

  return Array.from(grouped.entries())
    .map(([id, filters]) => ({
      id,
      label: id === "default" ? "Filters" : prettifyLabel(id),
      filters: filters.sort((a, b) => (a.order || 0) - (b.order || 0)),
    }))
    .sort((a, b) => {
      // Default group always first
      if (a.id === "default") return -1;
      if (b.id === "default") return 1;
      return 0;
    });
}

/**
 * Validates a filter configuration
 */
export function validateFilterConfig(config: FilterFieldConfig): string[] {
  const errors: string[] = [];

  if (!config.fieldName) {
    errors.push("fieldName is required");
  }

  if (!config.label) {
    errors.push("label is required");
  }

  if (!config.type) {
    errors.push("type is required");
  }

  if (config.type === "range" && config.rangeConfig) {
    if (
      config.rangeConfig.min !== undefined &&
      config.rangeConfig.max !== undefined &&
      config.rangeConfig.min > config.rangeConfig.max
    ) {
      errors.push("range min cannot be greater than max");
    }
  }

  return errors;
}
